import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import client, { setToken, getToken } from "@/lib/api";
import { toast } from "sonner";
import { syncPushIfGranted } from "@/lib/push";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AUTH_URL = "https://auth.emergentagent.com/";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function extractSessionId(url) {
  if (!url) return null;
  const m = url.match(/[?#&]session_id=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const processed = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const boot = async () => {
      const sid = extractSessionId(window.location.href);
      if (sid && !processed.current) {
        processed.current = true;
        setSigningIn(true);
        try {
          const { data } = await client.post("/auth/session", { session_id: sid });
          setToken(data.session_token);
          setUser(data.user);
        } catch (e) {
          const msg = e?.response?.status === 403
            ? (e?.response?.data?.detail || "Access is restricted to invited TilesMate team members.")
            : "Sign in failed. Please try again.";
          toast.error(msg);
        } finally {
          window.history.replaceState({}, "", window.location.pathname);
          setSigningIn(false);
          setLoading(false);
        }
        return;
      }
      if (getToken()) {
        await refresh();
      }
      setLoading(false);
    };
    boot();
  }, [refresh]);

  const login = useCallback(() => {
    setSigningIn(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `${AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await client.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (user) syncPushIfGranted();
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, signingIn, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}
